import { useReactFlow, NodeProps } from '@xyflow/react'
import { Steps } from 'intro.js-react'
import {
  RefreshCw,
  Book,
  HelpCircle,
  Copy,
} from 'lucide-react'
import React, { memo, useRef, useEffect, useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import '~/styles/introjs.css'


import { useUser } from '@/context/UserContext'
import useDataSettings from '@/hooks/useDataSettings'
import useDocumentState from '@/hooks/useDocumentState'
import useIntroTour from '@/hooks/useIntroTour'
import useSectionManagement from '@/hooks/useSectionManagement'

import {
  copyToClipboard,
  extractSectionsFromMarkdown,
} from '@/utils/documentUtils'
import styles from '@/styles/NodeDocument.module.css'
import DocumentLibraryDialog from '../../dialogs/document-library-dialog/DocumentLibraryDialog'
import DocumentStyleSelector from '../../document/DocumentStyleSelector'
import DocumentTabs from '../../document/DocumentTabs'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeConfigurationDialog, { NodeConfiguration, } from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'
import NodeExports from '../export-to/NodeExportTo'
import NodeReferenceSelectorDialog from '../../dialogs/reference-selector-dialog/NodeReferenceSelectorDialog'
import NodeTags from '../tags/NodeTags'
import UpdateWithAIDialog from '../../dialogs/UpdateWithAIDialog'

interface DocumentNodeData {
  contentMarkdown?: string
  documentDescription?: string
  statusMessage?: string
  isMarkdownExpanded?: boolean
  assignedEmails?: string[]
  configuration?: DocumentNodeConfiguration
  label?: string
  selectedSection?: Section | null
  sectionStates?: Record<string, "none" | "accepted" | "rejected" | "pending">
  processingStates?: Record<string, boolean>
  protectedSections?: Record<string, boolean>
  documentStyle?: any
  context?: string
  linkedKnowledge?: string
  dataSettings?: NodeDataSettings
  printSettings?: {
    headerText: string
    footerText: string
    coverMarkdown: string
  }
  sectionCommand?: {
    command: 'Draft' | 'Detailed' | 'Styled'
    sectionId: string
    sectionTitle: string
    content: string
    processing?: boolean
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    for_review?: string
  }
  isMinimized?: boolean
  isLocked?: boolean
  inputdata?: Record<string, Record<string, { value: string; enabled: boolean }>>
  documentCommand?: {
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    mode?: string
    processing?: boolean
    error?: string
    processedAt?: string
    message?: string
  }
}

interface DocumentNodeConfiguration extends NodeConfiguration {
  markdownFormatVisible: boolean
}

interface Section {
  id: string
  title: string
  content: string
}

interface NodeDataSettings {
  inputLinks: any[]
  outputLinks: any[]
  availableNodes: any[]
}

const NodeDocument: React.FC<NodeProps & { data: DocumentNodeData }> = (
  props
) => {
  const { id, data, selected } = props

  // Track selection state for debugging when needed
  // useEffect(() => {
  //   console.log('🔍 NodeDocument selection state:', { nodeId: id, selected })
  // }, [id, selected])

  const { updateNodeData } = useReactFlow()
  const nodeBaseContainerRef = useRef<HTMLDivElement | null>(null)
  const markdownPreviewRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // --- Local state initialization (safe defaults, not from data directly) ---
  // Remove any direct initialization from data.<field> here, only use useEffect to sync from collaborative state
  const [minimizeLevel, setMinimizeLevel] = useState<
    'normal' | 'medium' | 'minimum'
  >('normal')

  // --- Sync local state from collaborative state (data.<field>) ---
  // Only update local state from collaborative state in useEffect, one field at a time
  useEffect(() => {
    setMinimizeLevel((data.minimizeLevel as 'normal' | 'medium' | 'minimum') || 'normal')
  }, [data.minimizeLevel])

  const {
    contentMarkdown,
    setContentMarkdown,
    statusMessage,
    setStatusMessage,
    assignedEmails,
    setAssignedEmails,
    isLocked,
    setIsLocked,
    isMinimized,
    setIsMinimized,
    isMarkdownExpanded,
    setIsMarkdownExpanded,
    nodeConfig,
    setNodeConfig,
    documentStyle,
    setDocumentStyle,
    context,
    setContext,
    linkedKnowledge,
    setLinkedKnowledge,
    isConfigDialogVisible,
    setIsConfigDialogVisible,
    isDocLibraryDialogVisible,
    setIsDocLibraryDialogVisible,
    isUpdateWithAIDialogVisible,
    setIsUpdateWithAIDialogVisible,
    isDataSettingsDialogVisible,
    setIsDataSettingsDialogVisible,
    showDebugInfo,
    setShowDebugInfo,
    copyFeedback,
    setCopyFeedback,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleUpdateWithAI,
    handleLoadDocumentTemplates,
    handleStyleChange,
    handleContentChange,
    handleContextChange,
    handleLinkedKnowledgeChange,
  } = useDocumentState(id, data, updateNodeData)
  const {
    sections,
    selectedSectionId,
    setSelectedSectionId,
    editingSectionContent,
    setEditingSectionContent,
    isEditingSection,
    setIsEditingSection,
    sectionStates,
    setSectionStates,
    sectionReviews,
    setSectionReviews,
    handleSectionSelect,
    handleSectionEdit,
    handleSectionEditSave,
    handleStartEditing,
    handleCancelEditing,
    handleSectionStateChange,
    handleAcceptReview,
    handleRejectReview,
  } = useSectionManagement(
    id,
    data,
    syncNodeData,
    contentMarkdown,
    setContentMarkdown
  )

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
    generateLinkedKnowledgeText,
    prepareSectionsForDataSettings,
    prepareExposedDataFields,
    fetchAvailableDocumentNodes,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    showDebugInfo,
    context,
    sections,
    contentMarkdown,
    setIsDataSettingsDialogVisible
  )

  const { introEnabled, setIntroEnabled, introSteps, introOptions } =
    useIntroTour(id, selectedSectionId)

  // --- Set References functionality ---
  // Only local UI state here, correctly named
  const [isReferenceDialogVisible, setIsReferenceDialogVisible] =
    useState(false)
  const [availableNodes, setAvailableNodes] = useState<any[]>([])

  // Function to compute available nodes dynamically from flow
  const computeAvailableNodes = () => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    return allNodes
      .filter((n: any) => n.id !== id && n.data)
      .map((n: any) => {
        const dataPoints: {
          id: string
          label: string
          description?: string
        }[] = []

        // Add full document if exists
        if (n.data?.dataSettings?.documentSections?.fullDocument) {
          dataPoints.push({ id: 'fullDocument', label: 'Full Document' })
        }

        // Add all sections if exist
        const sections = n.data?.dataSettings?.documentSections?.sections || {}
        Object.entries(sections).forEach(([title, section]: [string, any]) => {
          dataPoints.push({ id: `section:${title}`, label: title })
        })

        // Add all exposed data points if exist
        const exposedData = n.data?.dataSettings?.exposedData || {}
        Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
          dataPoints.push({
            id: `exposed:${key}`,
            label: field.label || key,
            description: field.description || '',
          })
        })

        return {
          id: n.id,
          label: n.data?.label || n.id,
          dataPoints,
        }
      })
  }

  // Update availableNodes when dialog is opened
  const handleOpenReferenceDialog = () => {
    setAvailableNodes(computeAvailableNodes())
    setIsReferenceDialogVisible(true)
  }

  // Save references handler: store in node data and update context
  const handleSaveReferences = (
    selected: Record<string, string[]>,
    isLoading = false
  ) => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []

    let combined = ''

    Object.entries(selected).forEach(([nodeId, dataPointIds]) => {
      const node = allNodes.find((n: any) => n.id === nodeId)
      if (!node) return

      dataPointIds.forEach((dpId) => {
        let content = ''
        let sectionHeader = dpId

        if (dpId === 'fullDocument') {
          content = node.data?.contentMarkdown || node.data?.content || ''
          sectionHeader = 'fullDocument'
        } else if (dpId.startsWith('section:')) {
          const sectionName = dpId.replace('section:', '')
          content =
            node.data?.dataSettings?.documentSections?.sections?.[sectionName]
              ?.value || ''
          sectionHeader = `section:${sectionName}`
        } else if (dpId.startsWith('exposed:')) {
          const key = dpId.replace('exposed:', '')
          content = node.data?.dataSettings?.exposedData?.[key]?.value || ''
          sectionHeader = `exposed:${key}`
        }

        combined += `\n::: section ${sectionHeader}\n${content}\n:::\n`
      })
    })

    // Only update collaborative state via syncNodeData, do not update any local state for these fields here
    syncNodeData({
      context: combined,
      ...(isLoading ? {} : { setReferences: selected }),
    })
  }

  // Refresh imported data
  const handleRefreshLinkedKnowledge = () => {
    handleSaveReferences((data.setReferences as Record<string, string[]>) || {}, true)
  }

  const customButtons = [
    {
      id: '4',
      onClick: () => {
        setIsUpdateWithAIDialogVisible(true)
      },
      icon: <RefreshCw size={16} />,
      tooltip: 'Update with AI',
    },
    {
      id: '5',
      onClick: () => setIsDocLibraryDialogVisible(true),
      icon: <Book size={16} />,
      tooltip: 'Load Template',
    },
    {
      id: '6',
      onClick: () => {
        console.log('🔧 MANUAL RESET: Clearing all processing states and commands')
        syncNodeData({
          sectionCommand: null,
          processingStates: {}
        })
      },
      icon: <RefreshCw size={16} />,
      tooltip: 'Reset Processing States',
    },
    {
      id: '7',
      onClick: () => setIntroEnabled(true),
      icon: <HelpCircle size={16} />,
      tooltip: 'Help & Guide',
    },
  ]

  const documentConfigContent = (
    config: NodeConfiguration,
    setConfig: (config: NodeConfiguration) => void
  ) => (
    <div className="flex items-center space-x-2 mt-4">
      <Checkbox
        id="markdownFormatVisible"
        checked={config.markdownFormatVisible}
        onCheckedChange={(checked) =>
          setConfig({ ...config, markdownFormatVisible: checked === true })
        }
        className="w-4 h-4 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
      />
      <Label htmlFor="markdownFormatVisible" className="text-gray-700">
        Show Markdown format tab
      </Label>
    </div>
  )

  const toggleMarkdownExpand = () => setIsMarkdownExpanded(!isMarkdownExpanded)

  const handleCopyToClipboard = () =>
    copyToClipboard(contentMarkdown, markdownPreviewRef, setCopyFeedback)
  // Handler for toggling explanation minimize/maximize state
  const handleToggleExplanation = (sectionId: string) => {
    const currentExplanationStates =
      (data.sectionExplanationStates as Record<string, boolean>) || {}
    const newState = !currentExplanationStates[sectionId]
    // Only update collaborative state via syncNodeData
    syncNodeData({
      sectionExplanationStates: {
        ...currentExplanationStates,
        [sectionId]: newState,
      },
    })
  }

  // Update to use the handleSectionSelect with section name
  const customHandleSectionSelect = (
    sectionId: string,
    sectionName: string,
    event: React.MouseEvent
  ) => {
    handleSectionSelect(sectionId, sectionName, event)

    // Use the existing flow section select mechanism
    const flowSectionSelect = (window as any).__flowSectionSelect
    if (flowSectionSelect) {
      flowSectionSelect(id, sectionId, sectionName, {
        clientX: event.clientX,
        clientY: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
        screenX: event.screenX,
        screenY: event.screenY,
        type: event.type,
      })
    }
  }

  // Handle section state change from the global menu system
  useEffect(() => {
    const handleGlobalSectionCommand = (event: CustomEvent) => {
      console.log('🎯 NodeDocument received flowSectionCommand event:', event.detail)
      const { nodeId, sectionId, command } = event.detail

      console.log('📋 NodeDocument validation:', {
        receivedNodeId: nodeId,
        currentNodeId: id,
        nodeIdMatch: nodeId === id,
        sectionId,
        command,
        sectionsSize: sections.size,
        sectionExists: sections.has(sectionId)
      })

      if (nodeId !== id) {
        console.log('❌ Event nodeId does not match current node, ignoring')
        return // Only handle commands for this node
      }

      // 🚨 CRITICAL: Check if section is already being processed
      const isAlreadyProcessing = data.processingStates?.[sectionId] === true
      const hasActiveCommand = data.sectionCommand?.sectionId === sectionId && data.sectionCommand?.processing === true
      
      // 🔧 FIX: Also check for corrupted sectionCommand that might be blocking
      const hasCorruptedCommand = data.sectionCommand && (
        !data.sectionCommand.sectionId || 
        !data.sectionCommand.command || 
        data.sectionCommand.processing === undefined
      )

      // Command blocking check (debug only when needed)
      // Clear corrupted commands before proceeding
      if (hasCorruptedCommand) {
        syncNodeData({
          sectionCommand: null,
          processingStates: {
            ...(data.processingStates || {}),
            [sectionId]: false
          }
        })
        return // Let the next event cycle handle the command creation
      }

      const section = sections.get(sectionId)
      if (!section) {
        console.error(`❌ Section ${sectionId} not found in sections map (${sections.size} sections available)`)
        return
      }

      // Set the section as processing
      const updatedProcessingStates = {
        ...(data.processingStates || {}),
        [sectionId]: true,
      }

      // Determine the correct sysagent command based on the command type
      const isExplanationCommand = command === 'Explain'
      const sysagentCmd = isExplanationCommand ? 'SectionExplanation-Process-Started' : 'SectionCommand-Process-Started'

      const sectionCommand = {
        command: command,
        sectionId: sectionId,
        sectionTitle: section.title,
        content: section.content,
        processing: true,
        'sysagent-cmd': sysagentCmd, // ✅ Use correct command type for CrafterProcessor
        'sysagent-response': '',
      }

      // Command created for processing

      // Update both processing states and section command
      syncNodeData({
        processingStates: updatedProcessingStates,
        sectionCommand: sectionCommand,
      })
    }

    // Listen for section commands from the global menu system
    window.addEventListener(
      'flowSectionCommand',
      handleGlobalSectionCommand as EventListener
    )

    return () => {
      window.removeEventListener(
        'flowSectionCommand',
        handleGlobalSectionCommand as EventListener
      )
    }
  }, [id, sections, data.processingStates, data.sectionCommand, syncNodeData])

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel((data.minimizeLevel as 'normal' | 'medium' | 'minimum') || 'normal')
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, updateNodeData])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (contentMarkdown && contentMarkdown.trim()) {
      const wordCount = contentMarkdown.trim().split(/\s+/).length
      minifiedInfo.content = `${wordCount} words`
    }

    if (data.documentStyle) {
      minifiedInfo.style = data.documentStyle
    }

    if (sections && sections.size > 0) {
      minifiedInfo.sections = `${sections.size} section${sections.size !== 1 ? 's' : ''}`
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }
  }, [
    contentMarkdown,
    data.documentStyle,
    sections,
    data.minifiedInfo,
    id,
    updateNodeData,
  ])

  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  const nodeStatus = useMemo(() => {
    // Add your logic here based on your node's AI/processing state
    let status: 'success' | 'loading' | 'error' | 'initial' = 'initial'
    if (data?.statusMessage?.toLowerCase().includes('loading'))
      status = 'loading'
    else if (data?.statusMessage?.toLowerCase().includes('error'))
      status = 'error'
    else if (data?.statusMessage?.toLowerCase().includes('complete'))
      status = 'success'
    else if (data?.statusMessage?.toLowerCase().includes('successful'))
      status = 'success'
    return status
  }, [data.statusMessage])

  // Check and set default configuration if missing
  useEffect(() => {
    if (!data.configuration) {
      const defaultConfig: DocumentNodeConfiguration = {
        isAutomated: false,
        modifierprompt: '',
        markdownFormatVisible: false,
      }
      setNodeConfig(defaultConfig)
      syncNodeData({ configuration: defaultConfig })
    }
  }, [data.configuration, setNodeConfig, syncNodeData])

  // 🔧 FIX: Clean up stale processing states and commands on mount
  useEffect(() => {
    const hasCorruptedCommand = data.sectionCommand && (
      !data.sectionCommand.sectionId || 
      !data.sectionCommand.command || 
      data.sectionCommand.processing === undefined
    )

    const hasStaleProcessingStates = data.processingStates && 
      Object.values(data.processingStates).some(state => state === true)

    if (hasCorruptedCommand || hasStaleProcessingStates) {
      // Clear everything to start fresh
      syncNodeData({
        sectionCommand: null,
        processingStates: {}
      })
    }
  }, []) // Only run on mount

  const handlePrintSettingsChange = (settings: { headerText: string; footerText: string; coverMarkdown: string }) => {
    syncNodeData({ printSettings: settings })
  }

  return (
    <>
      <NodeBaseContainer
        resizable={!isLocked && !data.documentCommand?.processing}
        minWidth={100}
        minHeight={30}
        id={id}
        data={data}
        isProcessing={data.documentCommand?.processing || false}
        ref={nodeBaseContainerRef}
        className={`${styles.markdownPreview}`}
        status={nodeStatus}
        minimizeLevel={minimizeLevel}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        nodeTitle={data.label || 'Untitled Document'}
        isLocked={isLocked}
        isMinimized={isMinimized}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onTitleChange={(title) => syncNodeData({ label: title })}
        customButtons={customButtons}
        assignedEmails={
          Array.isArray(assignedEmails)
            ? assignedEmails
            : assignedEmails
              ? assignedEmails.trim().split(/\s+/).filter(Boolean)
              : []
        }
        onAssignUsersClick={() => {
          const emailsStr = window.prompt(
            'Enter emails separated by space',
            Array.isArray(assignedEmails)
              ? assignedEmails.join(' ')
              : assignedEmails || ''
          )
          if (emailsStr !== null) {
            const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
            setAssignedEmails(emailsArr)
            syncNodeData({ assignedEmails: emailsArr })
          }
        }}
        onDataSettingsClick={handleOpenDataSettingsDialog}
        onConfigClick={() => setIsConfigDialogVisible(true)}
        onToggleDebugInfo={() => setShowDebugInfo((prev) => !prev)}
        showDebugInfo={showDebugInfo}
        selected={selected} // ✅ CRITICAL: Make sure selected prop is passed!
        // Analysis & Recommendations props
        analysisAndRecommendations={Boolean(data.analysisAndRecommendations)}
        analysisResults={data.analysisResults}
        lastAnalyzedAt={data.lastAnalyzedAt as string}
        onAnalysisToggle={(enabled) =>
          syncNodeData({ analysisAndRecommendations: enabled })
        }
        onAnalysisRun={async () => {
          try {
            console.log('Running analysis for node:', id)

            // Get previous and current content for comparison
            const previousContent = '' // TODO: Get previous version from history
            const currentContent = contentMarkdown

            // Mock analysis call - replace with actual service call when backend is ready
            const mockAnalysisResult = {
              qualityRating: 'good',
              score: 78,
              scoreExplanation:
                'Content shows good structure and clarity with minor areas for improvement',
              assessment: {
                clarity: {
                  score: 20,
                  explanation:
                    'Text is generally clear but could benefit from more concise language',
                  recommendations: [
                    'Use shorter sentences',
                    'Define technical terms',
                  ],
                },
                completeness: {
                  score: 18,
                  explanation:
                    'Most sections are complete but missing some detail',
                  recommendations: [
                    'Add more examples',
                    'Expand on key concepts',
                  ],
                },
                relevance: {
                  score: 22,
                  explanation:
                    'Content stays on topic and addresses main objectives',
                  recommendations: [],
                },
                accuracy: {
                  score: 18,
                  explanation: 'Generally accurate with minor factual issues',
                  recommendations: ['Verify statistics', 'Update references'],
                },
              },
              recommendations: [
                'Improve sentence structure for better readability',
                'Add more supporting examples',
                'Verify all statistical claims',
              ],
            }

            syncNodeData({
              lastAnalyzedAt: new Date().toISOString(),
              analysisResults: mockAnalysisResult,
            })

            console.log('Analysis completed for node:', id)
          } catch (error) {
            console.error('Analysis failed:', error)
            syncNodeData({
              analysisResults: {
                qualityRating: 'error',
                score: 0,
                scoreExplanation: 'Analysis failed due to technical error',
                error: error instanceof Error ? error.message : 'Unknown error',
              },
            })
          }
        }}
      >
        {(minimizeLevel || 'normal') === 'normal' && (
          <div className="flex flex-col h-full">
            {!isMinimized && !isLocked && !data.documentCommand?.processing && (
              <div className={styles.dataInputWrapper}>
                {/* <NodeDataInput /> <NodeModifiersInput /> */}
              </div>
            )}
            {!isMinimized && !isLocked && !data.documentCommand?.processing && (
              <div className={styles.dataOutputWrapper}>
                {/* <NodeDataOutput /> */}
              </div>
            )}
            <div
              className={styles.nodeDocument}
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div className={styles.nodeHeader}>
                {!isLocked && !data.documentCommand?.processing && <NodeTags />}
              </div>
              <div
                className={styles.nodeContent}
                style={{
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {!isLocked && !data.documentCommand?.processing && (
                  <NodeExports />
                )}
                <div className={styles.nodeMainContent}>
                  {!isMinimized && (
                    <div
                      className={`${styles.contentStack} w-full`}
                      style={{
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        style={{
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '8px',
                          flex: '0 0 auto',
                        }}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleOpenReferenceDialog}
                          disabled={
                            isLocked || data.documentCommand?.processing
                          }
                        >
                          Set Data Link(s)
                        </Button>
                        <DocumentStyleSelector
                          documentStyle={documentStyle}
                          onStyleChange={handleStyleChange}
                          disabled={
                            isLocked || data.documentCommand?.processing
                          }
                        />
                        <div className={styles.actionButtonsContainer}>
                          <div className="flex items-center space-x-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={handleCopyToClipboard}
                                    className="copy-button"
                                    variant="outline"
                                  >
                                    {copyFeedback ? (
                                      copyFeedback
                                    ) : (
                                      <>
                                        <Copy className="h-4 w-4 mr-2" />
                                        Copy
                                      </>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Copy to clipboard</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={handleUpdateWithAI}
                                    className="update-ai-button"
                                    variant="outline"
                                    disabled={
                                      isLocked ||
                                      data.documentCommand?.processing
                                    }
                                  >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Update with AI
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Update with AI</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          flex: '1 1 auto',
                          overflowY: 'auto',
                          minHeight: '100px',
                        }}
                      >
                        <div className="w-full border-t border-gray-300 my-2" />
                        <DocumentTabs
                          contentMarkdown={contentMarkdown}
                          onContentChange={handleContentChange}
                          nodeId={id}
                          context={context}
                          onContextChange={handleContextChange}
                          linkedKnowledge={linkedKnowledge}
                          onLinkedKnowledgeChange={handleLinkedKnowledgeChange}
                          isMarkdownExpanded={isMarkdownExpanded}
                          markdownHeight={
                            textareaRef.current?.scrollHeight
                              ? `${textareaRef.current.scrollHeight}px`
                              : 'auto'
                          }
                          textareaHeight={
                            textareaRef.current?.scrollHeight
                              ? `${textareaRef.current.scrollHeight}px`
                              : 'auto'
                          }
                          nodebaseHeight={
                            nodeBaseContainerRef.current?.offsetHeight
                              ? `${nodeBaseContainerRef.current.offsetHeight - 200}px`
                              : 'auto'
                          }
                          markdownFormatVisible={nodeConfig.markdownFormatVisible}
                          onSectionSelect={customHandleSectionSelect}
                          sectionStates={sectionStates}
                          processingStates={data.processingStates || {}}
                          protectedSections={data.protectedSections || {}}
                          sectionReviews={sectionReviews}
                          sectionExplanations={
                            (data.sectionExplanations as Record<string, string>) || {}
                          }
                          sectionExplanationStates={
                            (data.sectionExplanationStates as Record<string, boolean>) || {}
                          }
                          onToggleExplanation={handleToggleExplanation}
                          onAcceptReview={handleAcceptReview}
                          onRejectReview={handleRejectReview}
                          markdownPreviewRef={markdownPreviewRef}
                          onRefreshLinkedKnowledge={() =>
                            generateLinkedKnowledgeText(dataSettings)
                          }
                          printSettings={data.printSettings}
                          onPrintSettingsChange={handlePrintSettingsChange}
                          data={data}
                        />
                      </div>
                      {statusMessage && (
                        <div className={styles.statusLabel}>
                          {statusMessage}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Pass currentNodeId and updateNodeData to the dialog */}
        {/* <NodeAnalysisAndRecommendationsDialog
          open={Boolean(data.analysisAndRecommendations)}
          onOpenChange={(open) =>
            syncNodeData({ analysisAndRecommendations: open })
          }
          analysisResults={data.analysisResults}
          onCreateRecommendedNodes={async () => {}} // No-op, handled in dialog now
          onCopyToContext={() => {}} // No-op, handled in dialog now
          currentNodeId={id}
          updateNodeData={updateNodeData}
        /> */}
      </NodeBaseContainer>

      <UpdateWithAIDialog
        visible={
          isUpdateWithAIDialogVisible && !data.documentCommand?.processing
        }
        onHide={() => setIsUpdateWithAIDialogVisible(false)}
        onUpdateWithAI={handleUpdateWithAI}
        contentMarkdown={contentMarkdown}
        documentStyle={documentStyle}
        context={context}
        linkedKnowledge={linkedKnowledge}
        onDuplicate={() => (window as any).__flowDuplicateNode?.(id)}
      />

      <DocumentLibraryDialog
        visible={isDocLibraryDialogVisible && !data.documentCommand?.processing}
        onHide={() => setIsDocLibraryDialogVisible(false)}
        onDocumentsSelected={handleLoadDocumentTemplates}
        currentContent={contentMarkdown}
        withPreview={true}
      />

      <NodeDataSettingsDialog
        visible={
          isDataSettingsDialogVisible && !data.documentCommand?.processing
        }
        onHide={() => setIsDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={handleRemoveInputLink}
        onSyncInputLink={handleSyncInputLink}
        forceShowAvailableNodes={true}
        debug={showDebugInfo}
      />

      <NodeConfigurationDialog
        visible={isConfigDialogVisible && !data.documentCommand?.processing}
        onHide={() => setIsConfigDialogVisible(false)}
        configuration={nodeConfig}
        onSave={(config) => {
          setNodeConfig(config as DocumentNodeConfiguration)
          syncNodeData({ configuration: config as DocumentNodeConfiguration })
        }}
        additionalContent={documentConfigContent}
      />

      <Steps
        enabled={introEnabled && !data.documentCommand?.processing}
        steps={introSteps}
        initialStep={0}
        onExit={() => setIntroEnabled(false)}
        options={introOptions}
      />

      {/* Set References Dialog */}
      <NodeReferenceSelectorDialog
        visible={isReferenceDialogVisible}
        onHide={() => setIsReferenceDialogVisible(false)}
        availableNodes={availableNodes}
        onSave={handleSaveReferences}
        initialSelected={data.setReferences as Record<string, string[]> | undefined}
      />
    </>
  )
}

export default memo(NodeDocument)
